import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { App, Card, Modal, Typography, Spin, Grid, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getDaysByValues } from "../../lib/industries";
import { fetchHrSettings } from "./components/hrSettingsThunks";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const HrSettingsView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { message } = App.useApp();
    const screens = useBreakpoint();

    const { loading, data, error, success } = useSelector(
        (state) => state.hrSettings
    );
    const authInfo = useSelector((state) => state.auth?.auth_info);
    const isIndia = authInfo?.country === "India";

    const [hrModuleModal, setHrModuleModal] = useState(false);

    useEffect(() => {
        dispatch(fetchHrSettings());
    }, [dispatch]);

    useEffect(() => {
        if (error) message.error(error);
        if (success) message.success(success);
    }, [error, success, message]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                padding: screens.sm ? "24px" : "12px",
                background: "#fafafa",
                minHeight: "100vh",
            }}
        >
            <Card
                title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
                            onClick={() => navigate(-1)}
                            data-testid="hr-module-settings-back-button"
                        />
                        <Title level={3} style={{ margin: 0 }} id="hrModuleSettingsTitle">HR Module Settings</Title>
                    </div>
                }
                bordered={false}
                style={{
                    width: "100%",
                    maxWidth: "900px",
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                {loading ? (
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        {/* Responsive 2-column layout */}
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "16px",
                                marginTop: "16px",
                                width: "100%",
                            }}
                        >
                            <SettingRow
                                screens={screens}
                                label={isIndia ? "Casual Leave Type" : "Annual Leave Type"}
                                value={
                                    Number(data.annual_leave_type) === 1
                                        ? "Calendar Days"
                                        : Number(data.annual_leave_type) === 0
                                            ? "Working Days"
                                            : "N/A"
                                }
                            />

                            <SettingRow
                                screens={screens}
                                label={isIndia ? "Casual Leave Days" : "Annual Leave Days"}
                                value={data.annual_leave_days || "N/A"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Max Sick Leave Days"
                                value={data.sick_leave_days ?? "N/A"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Max Flexi Leave Days"
                                value={data.flexi_leave_days ?? "N/A"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Weekend Days"
                                value={getDaysByValues(data.weekend_days) || "N/A"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Late Deduction?"
                                value={data.enable_late_deduction === "yes" ? "Yes" : "No"}
                            />

                            {data.enable_late_deduction === "yes" && (
                                <>
                                    <SettingRow
                                        screens={screens}
                                        label="Late Deduction Type"
                                        value={
                                            data.late_deduction_type === "half_day"
                                                ? "Half Day"
                                                : data.late_deduction_type === "full_day"
                                                    ? "Full Day"
                                                    : data.late_deduction_type || "N/A"
                                        }
                                    />
                                    <SettingRow
                                        screens={screens}
                                        label="Late Deduction After"
                                        value={
                                            data.late_deduction_after_minutes
                                                ? `${data.late_deduction_after_minutes} Minutes`
                                                : "N/A"
                                        }
                                    />
                                    <SettingRow
                                        screens={screens}
                                        label="Late Deduction From"
                                        value={
                                            data.late_deduction_leave_type === "casual_leave"
                                                ? isIndia ? "Casual Leave" : "Annual Leave"
                                                : data.late_deduction_leave_type === "sick_leave"
                                                    ? "Sick Leave"
                                                    : data.late_deduction_leave_type === "emergency_leave"
                                                        ? "Emergency Leave (Unpaid)"
                                                        : data.late_deduction_leave_type || "N/A"
                                        }
                                    />
                                </>
                            )}

                            <SettingRow
                                screens={screens}
                                label="Unmarked Attendance Deduction?"
                                value={data.enable_unmarked_attendance_deduction === "yes" ? "Yes" : "No"}
                            />

                            {data.enable_unmarked_attendance_deduction === "yes" && (
                                <>
                                    <SettingRow
                                        screens={screens}
                                        label="Unmarked Attendance Deduction From"
                                        value={
                                            data.unmarked_attendance_deduction_type === "casual_leave"
                                                ? isIndia ? "Casual Leave" : "Annual Leave"
                                                : data.unmarked_attendance_deduction_type === "emergency_leave"
                                                    ? "Emergency Leave (Unpaid)"
                                                    : data.unmarked_attendance_deduction_type || "N/A"
                                        }
                                    />
                                    <SettingRow
                                        screens={screens}
                                        label="Notify Before Deducting"
                                        value={
                                            Number(data.unmarked_attendance_tolerance_days) > 0
                                                ? `${data.unmarked_attendance_tolerance_days} days`
                                                : "Immediate (no notice)"
                                        }
                                    />
                                </>
                            )}

                            <SettingRow
                                screens={screens}
                                label="Backdated Leave Allowed (Casual / Annual / Flexi)"
                                value={`${data.leave_backdate_days ?? 7} day(s)`}
                            />
                            <SettingRow
                                screens={screens}
                                label="Backdated Sick Leave Allowed"
                                value={`${data.sick_leave_backdate_days ?? 7} day(s)`}
                            />

                            <SettingRow
                                screens={screens}
                                label="Reset Accrued Leave?"
                                value={data.should_reset_accrued_leave === "yes" ? "Yes" : "No"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Carry Forward"
                                value={data.leaves_carry_forward ?? "N/A"}
                            />

                            <SettingRow
                                screens={screens}
                                label="Work Route Auto Checkout Duration (min)"
                                value={data.auto_checkout_duration_minutes ?? "N/A"}
                            />
                        </div>

                    </>
                )}
            </Card>

            {/* HR Module Edit Modal */}
            <Modal
                title="Edit HR Settings"
                open={hrModuleModal}
                onCancel={() => setHrModuleModal(false)}
                footer={null}
            >
                <p>Form content goes here...</p>
            </Modal>
        </div>
    );
};

// ----------------------
// 🔹 Reusable row component
// ----------------------
const SettingRow = ({ label, value, screens = {} }) => {
    const isMobile = !screens.sm;
    return (
        <div className="hr-setting-row"
            style={{
                padding: "16px 20px",
                borderRadius: "8px",
                background: "#fff",
                border: "1px solid #e5e5e5",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                width: isMobile ? "100%" : "80%",
                minHeight: "58px",
                gap: isMobile ? "4px" : "12px",
                flexWrap: "wrap",
            }}
        >
            <Text type="secondary" strong style={{ fontSize: "15px", whiteSpace: "normal", wordBreak: "break-word" }}>
                {label}
            </Text>

            <Text strong style={{ fontSize: "15px", whiteSpace: "normal", wordBreak: "break-word" }}>
                {value}
            </Text>
        </div>
    );
};


export default HrSettingsView;
